import { maxBy, uniqWith } from 'es-toolkit';
import React, { useState } from 'react';

import { formatDistributedForce, formatForce } from '@paragon/client-tools';
import {
  Direction2D,
  distributedForceEquals,
  LineSegment2D,
  Point2D,
  point2DEquals,
  Vector2D,
} from '@paragon/geometry-library';
import { hypotenuse2D, max, notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import solveStackingLoads from '../../library/geometryUtility/LoadVectorStackingSolverUtility';
import { prepareLoadForDrawing } from '../../library/loadProjectionUtilities';
import { getActiveComponentDesignLoadVisualScaler } from '../../store/selectors/getActiveComponentDesignLoadVisualScaler';
import { getAllLoadsForActiveComponentDesign } from '../../store/selectors/getLoadCasesForActiveComponentDesignSelector';
import { getVisibleLoads } from '../../store/selectors/loadVisibilitySelectors';
import { attemptToEliminateOverlap, LabelInfo } from '../adjustForOverlap';
import { getLabelSizeFromTextMesh, LabelSize, LabelVisual } from '../util/LabelVisual';
import {
  DistributedLoadVisual,
  getInitialTrapezoidalLoadLabelPosition,
  getInitialUniformLoadLabelPosition,
} from './DistributedLoadVisual';
import { getInitialPointLoadLabelPosition, PointLoadVisual } from './PointLoadVisual';

interface LabelSizes {
  pointLoadLabelSizes: { [loadGuid: string]: LabelSize | undefined };
  uniformLoadLabelSizes: { [loadGuid: string]: LabelSize | undefined };
  trapezoidalLoadStartLabelSizes: { [loadGuid: string]: LabelSize | undefined };
  trapezoidalLoadEndLabelSizes: { [loadGuid: string]: LabelSize | undefined };
}

const createGetMovementSegmentFromPosition = (allowedShift: Vector2D) => (position: Point2D) =>
  new LineSegment2D(position, position.translate(allowedShift));

export function LoadVisuals() {
  const scaler = useAppSelector(getActiveComponentDesignLoadVisualScaler);
  const showLoadsUnprojected = useAppSelector((state) => state.visualOptions.showLoadsUnprojected);
  const visibleLoads = useAppSelector(getVisibleLoads);
  const loads = visibleLoads.map(prepareLoadForDrawing(showLoadsUnprojected));
  const loadsOnComponentDesign = useAppSelector(getAllLoadsForActiveComponentDesign);
  const loadsToSearchForHighY = loadsOnComponentDesign.length ? loadsOnComponentDesign : visibleLoads;
  const highestLoadY = max(loadsToSearchForHighY.flatMap((load) => [load.start.y, load.end?.y ?? 0])) ?? 0;

  // The approach of just storing the sizes might not work if labels aren't perfectly centered (e.g., if characters are
  // different heights). However, anecdotally, this does not seem to be an issue.
  const [labelSizes, setLabelSizes] = useState<LabelSizes>({
    pointLoadLabelSizes: {},
    uniformLoadLabelSizes: {},
    trapezoidalLoadStartLabelSizes: {},
    trapezoidalLoadEndLabelSizes: {},
  });

  if (!loads.length) return null;

  const offsets = solveStackingLoads(loads, scaler, highestLoadY);

  const uniformLoads = loads
    .map(({ load }) => load)
    .filter((load) => load.distribution === 'Distributed')
    .filter((load) => distributedForceEquals(load.startMagnitude, load.endMagnitude))
    .map((load) => {
      const loadStartLocation = Point2D.fromObject(load.start).add(offsets[load.guid]);
      const loadEndLocation = Point2D.fromObject(load.end).add(offsets[load.guid]);
      const loadDirection = Direction2D.fromObject(load.direction);
      const arrowMagnitude = scaler(load.startMagnitude, load.distribution);

      const initialLabelPosition = getInitialUniformLoadLabelPosition(
        loadStartLocation,
        loadEndLocation,
        loadDirection,
        arrowMagnitude,
      );
      const labelSize = labelSizes.uniformLoadLabelSizes[load.guid];
      const movementSegment = LineSegment2D.fromPointObjects(loadStartLocation, loadEndLocation);
      const labelInfo: LabelInfo | null =
        initialLabelPosition && labelSize
          ? {
              id: load.guid,
              center: initialLabelPosition,
              size: labelSize,
              getMovementSegment: () => movementSegment,
            }
          : null;

      const magnitudeText = formatDistributedForce(load.startMagnitude);

      return { load, arrowMagnitude, labelInfo, magnitudeText };
    });

  const trapezoidalLoads = loads
    .map(({ load }) => load)
    .filter((load) => load.distribution === 'Distributed')
    .filter((load) => !distributedForceEquals(load.startMagnitude, load.endMagnitude))
    .map((load) => {
      const loadStartLocation = Point2D.fromObject(load.start).add(offsets[load.guid]);
      const loadEndLocation = Point2D.fromObject(load.end).add(offsets[load.guid]);
      const loadDirection = Direction2D.fromObject(load.direction);
      const startArrowMagnitude = scaler(load.startMagnitude, load.distribution);
      const endArrowMagnitude = scaler(load.endMagnitude, load.distribution);
      const loadSegmentDirection = Direction2D.fromPoints(loadStartLocation, loadEndLocation);

      const initialStartLabelPosition = getInitialTrapezoidalLoadLabelPosition(
        loadStartLocation,
        loadDirection,
        startArrowMagnitude,
        loadSegmentDirection,
        1,
      );
      const initialEndLabelPosition = getInitialTrapezoidalLoadLabelPosition(
        loadEndLocation,
        loadDirection,
        endArrowMagnitude,
        loadSegmentDirection,
        -1,
      );
      const startLabelSize = labelSizes.trapezoidalLoadStartLabelSizes[load.guid];
      const endLabelSize = labelSizes.trapezoidalLoadEndLabelSizes[load.guid];
      const movementSegment = LineSegment2D.fromPointObjects(loadStartLocation, loadEndLocation);
      const startLabelInfo: LabelInfo | null =
        initialStartLabelPosition && startLabelSize
          ? {
              id: `${load.guid}-start`,
              center: initialStartLabelPosition,
              size: startLabelSize,
              getMovementSegment: () => movementSegment,
            }
          : null;
      const endLabelInfo: LabelInfo | null =
        initialEndLabelPosition && endLabelSize
          ? {
              id: `${load.guid}-end`,
              center: initialEndLabelPosition,
              size: endLabelSize,
              getMovementSegment: () => movementSegment,
            }
          : null;

      const startMagnitudeText = formatDistributedForce(load.startMagnitude);
      const endMagnitudeText = formatDistributedForce(load.endMagnitude);

      return {
        load,
        startArrowMagnitude,
        endArrowMagnitude,
        startLabelInfo,
        endLabelInfo,
        startMagnitudeText,
        endMagnitudeText,
      };
    });

  const concentratedLoads = loads.map(({ load }) => load).filter((load) => load.distribution === 'Concentrated');

  const pointLoadApplications = uniqWith(
    concentratedLoads.map(({ start, direction }) => ({
      location: start,
      direction: Direction2D.fromObject(direction),
    })),
    (l1, l2) => point2DEquals(l1.location, l2.location) && l1.direction.equals(l2.direction),
  );

  const pointLoadLabelData = Object.fromEntries(
    pointLoadApplications.map((application) => {
      const loadsApplied = concentratedLoads.filter(
        ({ start: location, direction }) =>
          point2DEquals(location, application.location) &&
          application.direction.equals(Direction2D.fromObject(direction)),
      );

      const furthestLoad = maxBy(loadsApplied, (load) => {
        const { x, y } = offsets[load.guid];
        return hypotenuse2D(x, y);
      })!;

      const totalMagnitude = loadsApplied.reduce((sum, load) => sum + load.startMagnitude, 0);

      return [furthestLoad.guid, { totalMagnitude } as { totalMagnitude: number } | undefined];
    }),
  );

  const pointLoads = concentratedLoads.map((load) => {
    const labelData = pointLoadLabelData[load.guid];
    const loadLocation = Point2D.fromObject(load.start).add(offsets[load.guid]);
    const loadDirection = Direction2D.fromObject(load.direction);
    const arrowMagnitude = scaler(load.startMagnitude, load.distribution);

    const initialLabelPosition = getInitialPointLoadLabelPosition(loadLocation, loadDirection, arrowMagnitude);
    const labelSize = labelSizes.pointLoadLabelSizes[load.guid];
    const labelInfo: LabelInfo | 'noLabel' | null = !labelData
      ? 'noLabel'
      : initialLabelPosition && labelSize
        ? {
            id: load.guid,
            center: initialLabelPosition,
            size: labelSize,
            getMovementSegment: createGetMovementSegmentFromPosition(loadDirection.toVector(-6)),
          }
        : null;

    const magnitudeText = formatForce(labelData?.totalMagnitude ?? load.startMagnitude);

    return {
      loadGuid: load.guid,
      loadType: load.loadType,
      loadLocation,
      loadDirection,
      arrowMagnitude,
      labelInfo,
      magnitudeText,
    };
  });

  const allLabelInfoOrNulls = [
    ...uniformLoads.map((load) => load.labelInfo),
    ...trapezoidalLoads.flatMap((load) => [load.startLabelInfo, load.endLabelInfo]),
    ...pointLoads.map((load) => load.labelInfo),
  ];

  const allLabelInfo = allLabelInfoOrNulls.every(notNullish)
    ? allLabelInfoOrNulls.filter((info) => info && info !== 'noLabel')
    : null;
  const labelPositionsAdjustedForOverlap = allLabelInfo ? attemptToEliminateOverlap(allLabelInfo) : null;

  return (
    <>
      {uniformLoads.map(({ load, arrowMagnitude, magnitudeText }) => {
        // Position at origin until labels have been adjusted for overlap.
        const labelPosition =
          (labelPositionsAdjustedForOverlap ? labelPositionsAdjustedForOverlap[load.guid] : null) ?? Point2D.origin;

        return (
          <DistributedLoadVisual
            key={load.guid}
            load={load}
            offset={offsets[load.guid]}
            startArrowMagnitude={arrowMagnitude}
            endArrowMagnitude={arrowMagnitude}
            labels={
              <LabelVisual
                onTextSync={(textMesh) => {
                  setLabelSizes((labelSizes) => ({
                    ...labelSizes,
                    uniformLoadLabelSizes: {
                      ...labelSizes.uniformLoadLabelSizes,
                      [load.guid]: getLabelSizeFromTextMesh(textMesh),
                    },
                  }));
                }}
                fontSize={2.2}
                position={labelPosition.to3D().toArray()}
                // Hide until labels have been adjusted for overlap
                visible={!!labelPositionsAdjustedForOverlap}
              >
                {magnitudeText}
              </LabelVisual>
            }
          />
        );
      })}
      {trapezoidalLoads.map(
        ({ load, startArrowMagnitude, endArrowMagnitude, startMagnitudeText, endMagnitudeText }) => {
          // Position at origin until labels have been adjusted for overlap.
          const startLabelPosition =
            (labelPositionsAdjustedForOverlap ? labelPositionsAdjustedForOverlap[`${load.guid}-start`] : null) ??
            Point2D.origin;
          const endLabelPosition =
            (labelPositionsAdjustedForOverlap ? labelPositionsAdjustedForOverlap[`${load.guid}-end`] : null) ??
            Point2D.origin;

          return (
            <DistributedLoadVisual
              key={load.guid}
              load={load}
              offset={offsets[load.guid]}
              startArrowMagnitude={startArrowMagnitude}
              endArrowMagnitude={endArrowMagnitude}
              labels={
                <>
                  <LabelVisual
                    onTextSync={(textMesh) => {
                      setLabelSizes((labelSizes) => ({
                        ...labelSizes,
                        trapezoidalLoadStartLabelSizes: {
                          ...labelSizes.trapezoidalLoadStartLabelSizes,
                          [load.guid]: getLabelSizeFromTextMesh(textMesh),
                        },
                      }));
                    }}
                    fontSize={2.2}
                    position={startLabelPosition.to3D().toArray()}
                    // Hide until labels have been adjusted for overlap
                    visible={!!labelPositionsAdjustedForOverlap}
                  >
                    {startMagnitudeText}
                  </LabelVisual>
                  <LabelVisual
                    onTextSync={(textMesh) => {
                      setLabelSizes((labelSizes) => ({
                        ...labelSizes,
                        trapezoidalLoadEndLabelSizes: {
                          ...labelSizes.trapezoidalLoadEndLabelSizes,
                          [load.guid]: getLabelSizeFromTextMesh(textMesh),
                        },
                      }));
                    }}
                    fontSize={2.2}
                    position={endLabelPosition.to3D().toArray()}
                    // Hide until labels have been adjusted for overlap
                    visible={!!labelPositionsAdjustedForOverlap}
                  >
                    {endMagnitudeText}
                  </LabelVisual>
                </>
              }
            />
          );
        },
      )}
      {pointLoads.map(
        ({ loadGuid, loadLocation, loadDirection, arrowMagnitude, magnitudeText, loadType, labelInfo }) => {
          // Position at origin until labels have been adjusted for overlap.
          const labelPosition =
            (labelPositionsAdjustedForOverlap ? labelPositionsAdjustedForOverlap[loadGuid] : null) ?? Point2D.origin;

          return (
            <PointLoadVisual
              key={loadGuid}
              loadLocation={loadLocation}
              loadDirection={loadDirection}
              arrowMagnitude={arrowMagnitude}
              loadType={loadType}
              label={
                labelInfo === 'noLabel' ? null : (
                  <LabelVisual
                    onTextSync={(textMesh) => {
                      setLabelSizes((labelSizes) => ({
                        ...labelSizes,
                        pointLoadLabelSizes: {
                          ...labelSizes.pointLoadLabelSizes,
                          [loadGuid]: getLabelSizeFromTextMesh(textMesh),
                        },
                      }));
                    }}
                    fontSize={2.2}
                    position={labelPosition.to3D().toArray()}
                    // Hide until labels have been adjusted for overlap
                    visible={!!labelPositionsAdjustedForOverlap}
                  >
                    {magnitudeText}
                  </LabelVisual>
                )
              }
            />
          );
        },
      )}
    </>
  );
}
